import type { UserLoginController } from '@infra/controllers/user-login.controller';
import type { UserLogoutController } from '@infra/controllers/user-logout.controller';
import {
  LOGIN_EMAIL_PASS_MESSAGE,
  LOGIN_PAGE_BROADCAST_CHANNEL_NAME,
  LOGIN_SSO_MESSAGE,
  LOGIN_UNIFIED_MESSAGE,
  ROOT_PAGE_BROADCAST_CHANNEL_NAME,
} from '@main/config/login-page.config';
import { ControllerFactory } from '@main/factories/controller.factory';
import type { Logger } from '@main/utils/logger';
import type { UserSSOLoginController } from '../../sso/controllers/user-sso-login.controller';
import type { UserUnifiedLoginController } from '../../unifiedLogin/controllers/user-unified-login.controller';

export interface LoginPageEventPayload {
  email: string;
  password: string;
}

export interface LoginSSOPageEventPayload {
  code: string;
  redirectUrl: string;
}

export interface UnifiedLoginPageEventPayload {
  token: string;
  refreshToken: string;
}

export interface LoginPageEventData {
  type: 'SCV.LOGIN_EMAIL_PASS' | 'SCV.LOGIN_SSO' | 'SCV.LOGIN_UNIFIED';
  payload:
    | LoginPageEventPayload
    | LoginSSOPageEventPayload
    | UnifiedLoginPageEventPayload;
}

export class LoginPageEventListener {
  private loginPageChannel: BroadcastChannel;
  private rootChannel: BroadcastChannel;
  private emailPassLoginController: UserLoginController;
  private ssoLoginController: UserSSOLoginController;
  private unifiedLoginController: UserUnifiedLoginController;

  private logoutController: UserLogoutController;

  constructor(private logger: Logger) {
    this.emailPassLoginController =
      ControllerFactory.buildUserLoginController();
    this.ssoLoginController = ControllerFactory.buildUserSSOLoginController();
    this.unifiedLoginController =
      ControllerFactory.buildUserUnifiedLoginController();
    this.logoutController = ControllerFactory.buildUserLogoutController();
    this.loginPageChannel = new BroadcastChannel(
      LOGIN_PAGE_BROADCAST_CHANNEL_NAME,
    );
    this.rootChannel = new BroadcastChannel(ROOT_PAGE_BROADCAST_CHANNEL_NAME);
  }

  private sendError(
    errorMessage: string,
    payload: unknown,
    error?: unknown,
    loggerMessage?: string,
  ) {
    if (loggerMessage) {
      this.logger.error(loggerMessage, { payload, error });
    }
    this.loginPageChannel.postMessage({
      type: 'SCV.LOGIN_FAILED',
      payload: {
        error: errorMessage,
      },
    });
    this.logoutController.handle();
  }

  private async loginEventListener(event: MessageEvent<LoginPageEventData>) {
    const {
      data: { type, payload },
    } = event;

    const loggerMessage = `Action: Login page listener | Event: ${type}`;

    try {
      if (type === LOGIN_EMAIL_PASS_MESSAGE) {
        const response = await this.emailPassLoginController.handle(
          payload as LoginPageEventPayload,
        );
        if (response instanceof Error) {
          this.sendError(response.message, payload, response, loggerMessage);
        }
      } else if (type === LOGIN_SSO_MESSAGE) {
        const response = await this.ssoLoginController.handle(
          payload as LoginSSOPageEventPayload,
        );
        if (response instanceof Error) {
          this.sendError(response.message, payload, response, loggerMessage);
        }
      } else if (type === LOGIN_UNIFIED_MESSAGE) {
        const response = await this.unifiedLoginController.handle(
          payload as UnifiedLoginPageEventPayload,
        );
        if (response instanceof Error) {
          this.sendError(response.message, payload, response, loggerMessage);
        }
      } else {
        this.logger.warn(`${loggerMessage} | Unknown event`);
        this.sendError(
          'Unexpected error occurred. Try again.',
          payload,
          null,
          loggerMessage,
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        this.sendError(error.message, payload, error, loggerMessage);
      } else {
        this.sendError('Login failed', payload, error, loggerMessage);
      }
    }
  }

  addEventListener() {
    this.rootChannel.onmessage = (event) => this.loginEventListener(event);
  }
}
